import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useOutletContext, useLocation } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import cogoToast from '@successtar/cogo-toast'
import { useConfigStore } from '@renderer/stores/configState'
import Button from '@renderer/components/Button'
import { COUNTDOWN_PLACEHOLDER, isCountdownUrgent, useCountdown } from '@renderer/utils/countdownUtils'
import { Package } from '@renderer/types/configState'
import { FaVideo } from 'react-icons/fa'
import { IoPrint } from 'react-icons/io5'
import { MdOutlineGifBox, MdOutlineVideoLibrary, MdPhotoLibrary } from 'react-icons/md'
import { DeepPartial, SessionStateData } from '@renderer/types/sessionState'
import { useTranslation } from 'react-i18next'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { formatAmount, normalizeCurrency, roundMoney } from '@renderer/utils/formatCurrency'
import { isRecordingPackageType, toRecordingType } from '@renderer/utils/recordingType'
// import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const {
    id,
    setSessionState,
    transaction,
    userPreferences,
    mode,
    logActivity
  } = useSessionStateStore()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const { isCompact: compactCheckout, isPortrait: checkoutIsPortrait } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  /** Physical phone/tablet in landscape (short height) — real window, not design canvas */
  const checkoutNarrowLandscape = viewportCompact && isLandscapeViewport
  /** Dense row/chrome like design-compact, also when browser is narrow-landscape */
  const useCompactChrome = compactCheckout || checkoutNarrowLandscape
  /** Non-compact portrait (e.g. 1080×1920): needs larger text/panel to be readable from afar */
  const checkoutPortrait = !useCompactChrome && checkoutIsPortrait
  /** Visible on frosted glass */
  const checkoutRule = 'border-gray-400'
  const checkoutPkgIconClass = useCompactChrome
    ? checkoutNarrowLandscape
      ? `rounded-sm border ${checkoutRule} p-0.5 text-xl font-medium text-carbon`
      : `rounded-sm border ${checkoutRule} p-1 text-3xl font-medium text-carbon`
    : checkoutPortrait
      ? `rounded-sm border ${checkoutRule} p-2.5 text-4xl font-medium text-carbon`
      : `rounded-sm border ${checkoutRule} p-2 text-3xl font-medium text-carbon`
  const checkoutH2Class = useCompactChrome
    ? checkoutNarrowLandscape
      ? 'text-sm font-medium text-carbon'
      : 'text-base font-medium text-carbon'
    : checkoutPortrait
      ? 'text-xl font-medium text-carbon'
      : 'text-lg font-medium text-carbon'
  const checkoutSubClass = useCompactChrome
    ? checkoutNarrowLandscape
      ? 'text-xs leading-tight text-carbon'
      : 'text-sm text-carbon'
    : checkoutPortrait
      ? 'text-lg text-carbon'
      : 'text-base text-carbon'
  const checkoutPriceSize = useCompactChrome
    ? checkoutNarrowLandscape
      ? 'text-sm'
      : 'text-base'
    : checkoutPortrait
      ? 'text-xl'
      : 'text-lg'
  const checkoutPriceClass = `${checkoutPriceSize} text-carbon`
  /**
   * Compact: grid keeps stepper/checkbox + price columns stable when quantity changes.
   * Default landscape: flex with equal flex-1 wings so the control sits centered between title and price (legacy).
   */
  const checkoutPkgRow = useCompactChrome
    ? checkoutNarrowLandscape
      ? 'grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(3.5rem,auto)] items-center gap-x-1 gap-y-0.5 py-0.5'
      : 'grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(4rem,auto)] items-center gap-x-1.5 gap-y-0.5 py-1.5'
    : checkoutPortrait
      ? // Grid, not justify-between: with flex the control x-position depended on
        // the title/price widths, so the add-on toggles zig-zagged row to row.
        'grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(6.5rem,auto)] items-center gap-x-4 py-4'
      : 'grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(5.5rem,auto)] items-center gap-x-3 py-3'
  const checkoutPkgLead = useCompactChrome
    ? checkoutNarrowLandscape
      ? 'flex min-w-0 items-center gap-1'
      : 'flex min-w-0 items-center gap-1.5'
    : checkoutPortrait
      ? 'flex min-w-0 items-center gap-4'
      : 'flex min-w-0 items-center gap-3'
  const checkoutPkgTitleBlock = 'min-w-0 flex-1 flex flex-col justify-center'
  const checkoutPkgControl = useCompactChrome
    ? 'flex flex-col items-center justify-center justify-self-center self-center'
    : 'flex shrink-0 flex-col items-center justify-center justify-self-center'
  const checkoutPkgPriceSpanAlign = useCompactChrome
    ? 'min-w-0 justify-self-end pl-1 text-right tabular-nums'
    : 'justify-self-end pl-1 text-right tabular-nums'
  const checkoutPkgToggleWrap = useCompactChrome
    ? 'flex shrink-0 justify-center justify-self-center px-0.5'
    : 'flex shrink-0 justify-center justify-self-center px-0.5'
  const checkoutStepperMinus = useCompactChrome
    ? checkoutNarrowLandscape
      ? `flex h-6 w-6 shrink-0 items-center justify-center border-r ${checkoutRule} text-base font-bold text-carbon hover:bg-gray-200`
      : `flex h-7 w-7 shrink-0 items-center justify-center border-r ${checkoutRule} text-xl font-bold text-carbon hover:bg-gray-200`
    : checkoutPortrait
      ? `flex h-14 w-14 shrink-0 items-center justify-center border-r ${checkoutRule} text-3xl font-bold text-carbon hover:bg-gray-200`
      : `flex h-8 w-8 shrink-0 items-center justify-center border-r ${checkoutRule} text-xl font-bold text-carbon hover:bg-gray-200`
  const checkoutStepperPlus = useCompactChrome
    ? checkoutNarrowLandscape
      ? `flex h-6 w-6 shrink-0 items-center justify-center border-l ${checkoutRule} text-base font-bold text-carbon hover:bg-gray-300`
      : `flex h-7 w-7 shrink-0 items-center justify-center border-l ${checkoutRule} text-xl font-bold text-carbon hover:bg-gray-300`
    : checkoutPortrait
      ? `flex h-14 w-14 shrink-0 items-center justify-center border-l ${checkoutRule} text-3xl font-bold text-carbon hover:bg-gray-300`
      : `flex h-8 w-8 shrink-0 items-center justify-center border-l ${checkoutRule} text-xl font-bold text-carbon hover:bg-gray-300`
  const checkoutStepperMid = useCompactChrome
    ? checkoutNarrowLandscape
      ? 'flex h-6 min-w-[1.5rem] flex-1 items-center justify-center px-0.5 text-sm font-medium tabular-nums text-carbon'
      : 'flex h-7 min-w-[1.75rem] flex-1 items-center justify-center px-0.5 text-lg font-medium tabular-nums text-carbon'
    : checkoutPortrait
      ? 'flex h-14 min-w-[3.5rem] flex-1 items-center justify-center px-1 text-2xl font-medium tabular-nums text-carbon'
      : 'flex h-8 min-w-[2rem] flex-1 items-center justify-center px-1 text-lg font-medium tabular-nums text-carbon'
  /** Subtotal/total labels = same scale as package row titles (checkoutH2Class) */
  const checkoutSummaryLabel = checkoutH2Class
  const checkoutTotalLabel = `${checkoutH2Class} font-semibold`
  /** Amounts = same type scale as package prices; strikethrough uses size + gray only */
  const checkoutSummaryAmountStruck = `${checkoutPriceSize} line-through text-gray-500`
  const checkoutTotalAmount = `${checkoutPriceClass} font-semibold`
  const checkoutDiscountLabel = useCompactChrome
    ? checkoutNarrowLandscape
      ? 'text-sm font-medium text-green-600'
      : 'text-base font-medium text-green-600'
    : checkoutPortrait
      ? 'text-xl font-medium text-green-600'
      : 'text-lg font-medium text-green-600'
  const checkoutDiscountAmt = `${checkoutPriceSize} font-semibold text-green-600`
  /**
   * Same padding, type, and min-height for primary + secondary so long labels (voucher) do not
   * read taller/larger than Proceed; Button base uses clamp() so we override with ! and flex centering.
   */
  const checkoutFooterBtn = useCompactChrome
    ? checkoutNarrowLandscape
      ? '!inline-flex !min-h-9 !w-full !items-center !justify-center !px-4 !py-2 !text-sm !leading-snug'
      : '!inline-flex !min-h-11 !w-full !items-center !justify-center !px-5 !py-2.5 !text-base !leading-snug'
    : checkoutPortrait
      ? '!inline-flex !min-h-[4rem] !w-3/4 !items-center !justify-center !px-8 !py-4 !text-2xl !leading-snug'
      : '!inline-flex !min-h-[3rem] !w-3/4 !items-center !justify-center !px-6 !py-3 !text-xl !leading-snug'
  const checkoutMainBtn = checkoutFooterBtn
  const checkoutSecondaryBtn = checkoutFooterBtn
  const checkoutTimeFooter = useCompactChrome
    ? checkoutNarrowLandscape
      ? 'text-center font-mono tabular-nums text-base text-carbon'
      : 'text-center font-mono tabular-nums text-lg text-carbon'
    : checkoutPortrait
      ? 'text-center font-mono tabular-nums text-xl text-carbon'
      : 'text-center font-mono tabular-nums text-lg text-carbon'
  const checkoutItemsListGap = checkoutNarrowLandscape ? 'gap-1' : useCompactChrome ? 'gap-2' : checkoutPortrait ? 'gap-6' : 'gap-4'
  const config = globalConfig?.pages?.checkout || { settings: { packages: [], checkoutTimeout: 0, promoEnabled: false, voucherEnabled: false } }
  const paymentConfig = globalConfig?.pages?.payment || { settings: { enabled: false } }
  const welcomeSettings = getPageSettings('welcome') || { startWithPayment: false }
  const location = useLocation()
  /**
   * Voucher-only customer flow: project's stamped payment tier has no QRIS
   * rail and the operator hasn't wired a custom gateway. Customers must
   * enter a voucher to proceed — cash voucher settles immediately,
   * percentage/fixed vouchers reduce the total but still need offline
   * settlement with the operator. Mirrors the backend's `global_hidden`
   * and (no-custom-gateway) `localized_in_progress` tiers, and also
   * triggers when Payment.tsx bounces back with `state.voucherOnly`.
   */
  const tier = globalConfig?.paymentSetupTier
  const hasCustomGateway = Boolean(globalConfig?.hasCustomGateway)
  // `global_hidden` (e.g. MXN) is voucher-only ONLY without a custom gateway;
  // a wired Mercado Pago makes online payment available. See view/Payment.tsx.
  const isVoucherOnlyRegion =
    (tier === 'global_hidden' || tier === 'localized_in_progress') &&
    !hasCustomGateway
  const arrivedFromVoucherOnlyRedirect = Boolean(
    (location.state as { voucherOnly?: boolean } | null)?.voucherOnly
  )
  const [showPromoInput, setShowPromoInput] = useState(
    arrivedFromVoucherOnlyRedirect &&
      (config?.settings?.voucherEnabled ?? config?.settings?.promoEnabled ?? false)
  )
  const [promoCode, setPromoCode] = useState('')
  const [promoError, setPromoError] = useState<string>('')
  const [isPromoLoading, setIsPromoLoading] = useState(false)
  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const [isProcessingVoucher, setIsProcessingVoucher] = useState(false)
  const [voucherProcessingCountdown, setVoucherProcessingCountdown] = useState(3)
  // const { type: subscriptionType } = useSubscriptionStore()

  // Outlet context kept for forward-compat (other consumers read user info).
  // Currency is now driven by the project's stamped resolvedCurrency rather
  // than email-based country detection.
  useOutletContext<{ user: { email: string } } | undefined>()

  /**
   * Display currency for this project (write-time stamp from the backend's
   * `resolveLocalCurrency(country)`). Legacy projects without a stamp render
   * as IDR via the normalize default.
   */
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const formatPrice = (amount: number | null | undefined): string =>
    formatAmount(amount, projectCurrency)

  /**
   * Editor-driven per-frame add-on written on the Frame page. Added ONCE per
   * order (not × copies) when a print package is selected in capture mode.
   * Defensive: when payment is taken before frame selection the total can't
   * carry a per-frame amount, so treat as 0 (mirrors the server's gating).
   */
  const frameAddon = welcomeSettings?.startWithPayment
    ? 0
    : userPreferences.frameAddonPricePerPackage ?? 0

  /**
   * Editor-driven per-category base price for the selected frame's category.
   * Added ONCE per order alongside `frameAddon` (independent — they can stack),
   * only when a print package is selected. Same `!startWithPayment` gate as the
   * add-on; the server also returns 0 unless the base-price feature is gated on.
   */
  const frameCategoryBase = welcomeSettings?.startWithPayment
    ? 0
    : userPreferences.frameCategoryBasePricePerOrder ?? 0

  // Memoize packages calculations
  const packages = useMemo(() => config?.settings.packages || [], [config])
  const printPackages = useMemo(
    () =>
      mode === 'capture'
        ? packages
            .filter((pkg) => pkg.type === 'print')
            .sort((a, b) => (a.quantity || 0) - (b.quantity || 0))
        : [],
    [mode, packages]
  )
  const gifPackage = useMemo(
    () => (mode === 'capture' ? packages.find((pkg) => pkg.type === 'gif') : null),
    [mode, packages]
  )
  const recordingPackage = useMemo(
    () =>
      mode === 'capture'
        ? packages.find(
            (pkg) =>
              pkg.type === 'recording' ||
              pkg.type === 'strip_live' ||
              pkg.type === 'strip_story' ||
              pkg.type === 'session_recording'
          )
        : null,
    [mode, packages]
  )
  const videoPackage = useMemo(
    // Record step retired: a configured video package is a normal capture add-on
    // (capture sessions produce framed videos) instead of silently vanishing.
    () => packages.find((pkg) => pkg.type === 'video') ?? null,
    [mode, packages]
  )
  const digitalPackage = useMemo(() => packages.find((pkg) => pkg.type === 'digital'), [packages])
  const paxPackages = useMemo(
    () =>
      mode === 'capture'
        ? packages
            .filter((pkg) => pkg.type === 'pax')
            .sort((a, b) => (a.quantity || 0) - (b.quantity || 0))
        : [],
    [mode, packages]
  )

  // Get recording type for display. `'recording'` maps to live mode for backward compatibility.
  const recordingType = useMemo(
    () => toRecordingType(recordingPackage?.type),
    [recordingPackage]
  )

  // Get recording description based on type
  const getRecordingDescription = (type: string): string => {
    switch (type) {
      case 'recording':
      case 'strip_live':
        return t('recording.description.strip_live')
      case 'strip_story':
        return t('recording.description.strip_story')
      case 'session_recording':
        return t('recording.description.session_recording')
      default:
        return t('recording.description.strip_live')
    }
  }

  // Initial navigation checks
  useEffect(() => {
    if (
      mode === 'capture' &&
      !printPackages.length &&
      !gifPackage &&
      !recordingPackage &&
      !digitalPackage
    ) {
      const target =
        welcomeSettings?.startWithPayment && paymentConfig?.settings?.enabled ? 'payment' : 'capture'
      navigate(`/${projectId}/${target}`)
    }
    if (mode === 'record' && !videoPackage) {
      navigate(`/${projectId}/record`)
    }
  }, [
    mode,
    printPackages,
    gifPackage,
    recordingPackage,
    videoPackage,
    navigate,
    projectId,
    digitalPackage,
    welcomeSettings,
    paymentConfig
  ])

  // Initialize selected print quantity and session state (allow 0 = no print)
  const [selectedPrintQuantity, setSelectedPrintQuantity] = useState<number>(
    printPackages.length > 0 ? printPackages[0].quantity : 1
  )
  const [selectedPaxQuantity, setSelectedPaxQuantity] = useState<number>(
    paxPackages[0]?.quantity || 1
  )

  // Initial session state setup
  useEffect(() => {
    if (config?.settings.packages?.length && !transaction.selectedPackages.length) {
      // Use a microtask to avoid render-phase updates
      Promise.resolve().then(() => {
        const hasPrint = printPackages.length > 0 && selectedPrintQuantity > 0
        const initialDigital = digitalPackage ? !digitalPackage.optional : !hasPrint
        const initialPax = paxPackages.length > 0

        setSessionState({
          userPreferences: {
            copies: selectedPrintQuantity,
            useGIF: gifPackage ? !gifPackage.optional : false,
            useRecording: recordingPackage ? !recordingPackage.optional : false,
            useVideo: videoPackage ? !videoPackage.optional : false,
            useDigital: initialDigital,
            usePrint: hasPrint,
            usePax: initialPax,
            recordingType: recordingType as
              | 'recording'
              | 'strip_live'
              | 'strip_story'
              | 'session_recording'
              | null
              | undefined
          }
        })
      })
    }
  }, [
    config,
    packages,
    selectedPrintQuantity,
    selectedPaxQuantity,
    gifPackage,
    recordingPackage,
    videoPackage,
    recordingType,
    digitalPackage,
    printPackages.length,
    paxPackages.length
  ])

  const getSelectedPrintPackage = (): Package | undefined => {
    return printPackages.find((pkg) => pkg.quantity === selectedPrintQuantity)
  }

  const getSelectedPaxPackage = (): Package | undefined => {
    return paxPackages.find((pkg) => pkg.quantity === selectedPaxQuantity)
  }

  // Calculate subtotal (before discount)
  const calculateSubtotal = (): number => {
    // Sum raw native prices (RM carries cents); round once to the currency's
    // minor unit at the end so we never drop ringgit cents per line item.
    let subtotal = 0
    if (paymentConfig?.settings?.enabled) {
      if (mode === 'capture') {
        if (printPackages.length > 0) {
          const selectedPrintPackage = getSelectedPrintPackage()
          subtotal += selectedPrintPackage?.price || 0
          // Per-frame add-on: added ONCE per order (not × copies), only when a
          // print package is actually selected (a printed strip carries the frame).
          if (selectedPrintPackage && selectedPrintQuantity > 0) {
            subtotal += frameAddon
            // Per-category base price: added ONCE per order alongside the add-on,
            // independent of it (they can stack). Not multiplied by copies.
            subtotal += frameCategoryBase
          }
        }
        if (gifPackage && userPreferences.useGIF) subtotal += gifPackage.price
        if (recordingPackage && userPreferences.useRecording) subtotal += recordingPackage.price
        if (digitalPackage && userPreferences.useDigital) {
          subtotal += digitalPackage.price
        }
        if (videoPackage && userPreferences.useVideo) {
          subtotal += videoPackage.price
        }
        if (paxPackages.length > 0 && userPreferences.usePax) {
          const selectedPaxPackage = getSelectedPaxPackage()
          subtotal += selectedPaxPackage?.price || 0
        }
      } else if (mode === 'record' && videoPackage && userPreferences.useVideo) {
        subtotal += videoPackage.price
      }
    }
    return roundMoney(subtotal, projectCurrency)
  }

  // Calculate total price (after discount)
  const calculateTotal = (): number => {
    const subtotal = calculateSubtotal()
    const discount = transaction.discount || 0
    return roundMoney(Math.max(0, subtotal - discount), projectCurrency)
  }

  const handleNext = async (): Promise<void> => {
    if (!transaction.selectedPackages) return

    // Calculate final amount (this is the source of truth)
    const finalAmount = calculateTotal()

    // Build selectedPackages array to match the actual calculation
    // This ensures Payment.tsx can verify the amount if needed
    const packagesInCalculation: Package[] = []
    if (mode === 'capture') {
      if (printPackages.length > 0) {
        const selectedPrintPackage = getSelectedPrintPackage()
        if (selectedPrintPackage) packagesInCalculation.push(selectedPrintPackage)
      }
      if (gifPackage && userPreferences.useGIF && gifPackage) {
        packagesInCalculation.push(gifPackage)
      }
      if (recordingPackage && userPreferences.useRecording && recordingPackage) {
        packagesInCalculation.push(recordingPackage)
      }
      if (digitalPackage && userPreferences.useDigital && digitalPackage) {
        packagesInCalculation.push(digitalPackage)
      }
      if (videoPackage && userPreferences.useVideo) {
        packagesInCalculation.push(videoPackage)
      }
      if (paxPackages.length > 0 && userPreferences.usePax) {
        const selectedPaxPackage = getSelectedPaxPackage()
        if (selectedPaxPackage) packagesInCalculation.push(selectedPaxPackage)
      }
    } else if (mode === 'record' && videoPackage && userPreferences.useVideo && videoPackage) {
      packagesInCalculation.push(videoPackage)
    }

    // Set user preferences based on selected quantities and toggles
    const details = {
      copies: selectedPrintQuantity,
      amount: finalAmount,
      package: packagesInCalculation.map((pkg) => pkg.type).join(', '),
      packageDescription: packagesInCalculation.map((pkg) => pkg.description).join(', '),
      promoApplied: Boolean(promoCode),
      promoCode: promoCode || undefined
    }
    logActivity('Checkout complete.', details)

    // Set amount only when not already settled (e.g. cash voucher). Do not overwrite amount for settlement.
    const subtotalForNext = calculateSubtotal()
    const discountForNext = transaction.discount || 0
    // `amountToSet` is persisted back with `currency: projectCurrency` (the
    // project's NATIVE currency), so it must always be a native figure.
    // On the settlement branch prefer `nativeAmount` over `transaction.amount`
    // — the latter can hold the gateway SETTLEMENT amount (e.g. IDR for an MYR
    // project on cross-border QRIS), which paired with the native currency
    // symbol would render a 3500× wrong price (the "RM 67,014" bug). Cash
    // vouchers store native here so `nativeAmount` may be absent; fall back to
    // `transaction.amount`, then the freshly computed subtotal.
    const amountToSet =
      transaction.status === 'settlement'
        ? transaction.nativeAmount ?? transaction.amount ?? subtotalForNext
        : roundMoney(Math.max(0, subtotalForNext - discountForNext), projectCurrency)

    console.log(
      `[CHECKOUT] Setting transaction amount: Rp${amountToSet} (discount: ${transaction.discount || 0}, subtotal: ${subtotalForNext}, status: ${transaction.status})`
    )
    console.log(
      `[CHECKOUT] Packages in calculation:`,
      packagesInCalculation.map((p) => `${p.type}: Rp${p.price}`)
    )
    setSessionState({
      userPreferences: {
        copies: selectedPrintQuantity,
        usePrint: selectedPrintQuantity > 0
      },
      transaction: {
        ...(transaction.status !== 'settlement' ? { amount: amountToSet } : {}),
        selectedPackages: packagesInCalculation,
        currency: projectCurrency
      }
    })
    console.log(`[CHECKOUT] Transaction amount set in state:`, {
      amount: amountToSet,
      discount: transaction.discount || 0,
      packagesCount: packagesInCalculation.length
    })

    // Ensure state update propagates before uploading to SQLite and queuing for cloud sync
    await Promise.resolve()

    // Delay session upload to allow more activity logs to be collected
    // This prevents premature upload before user completes their journey
    // The session will be uploaded after a delay or when the app/session ends
    setTimeout(() => {
      stateUploader.uploadSessionState(id)
    }, 5000) // 5 second delay to collect more logs

    // Check applyAfterCapture setting
    const filterSettings = getPageSettings('filter')
    const applyAfterCapture = filterSettings?.applyAfterCapture || false

    // If transaction status is already settlement (cash payment), skip payment page
    if (transaction.status === 'settlement') {
      const nextPage = welcomeSettings?.startWithPayment
        ? applyAfterCapture
          ? 'frame'
          : 'filter'
        : mode === 'capture'
          ? 'capture'
          : 'record'
      navigate(`/${projectId}/${nextPage}`)
      return
    }

    // Navigate based on startWithPayment setting, applyAfterCapture, and mode
    const nextPage = paymentConfig?.settings?.enabled
      ? calculateTotal() === 0
        ? welcomeSettings?.startWithPayment
          ? applyAfterCapture
            ? 'frame'
            : 'filter'
          : mode === 'capture'
            ? 'capture'
            : 'record'
        : 'payment'
      : welcomeSettings?.startWithPayment
        ? applyAfterCapture
          ? 'frame'
          : 'filter'
        : mode === 'capture'
          ? 'capture'
          : 'record'
    navigate(`/${projectId}/${nextPage}`)
  }

  // Initialize countdown with the useCountdown hook
  const checkoutTimeout = config?.settings?.checkoutTimeout || 0
  const countdown = useCountdown(checkoutTimeout)

  // Set up countdown callbacks
  useEffect(() => {
    if (checkoutTimeout > 0) {
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })
      countdown.setOnComplete(() => {
        const details = {
          selectedPackages: transaction.selectedPackages?.map((pkg) => pkg.type),
          amount: calculateTotal(),
          timeoutDuration: checkoutTimeout
        }
        logActivity('Checkout timed out.', details)
        handleNext()
      })
    }
  }, [
    checkoutTimeout,
    transaction.selectedPackages,
    setSessionState,
    transaction.discount,
    handleNext
  ])

  // Start the countdown on mount
  useEffect(() => {
    if (checkoutTimeout > 0) {
      countdown.start()

      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [checkoutTimeout])

  // Handle voucher processing countdown and navigation
  useEffect(() => {
    if (!isProcessingVoucher) return

    const interval = setInterval(() => {
      setVoucherProcessingCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          setIsProcessingVoucher(false)
          
          // Navigate using the exact same logic as handleNext
          const filterSettings = getPageSettings('filter')
          const applyAfterCapture = filterSettings?.applyAfterCapture || false
          
          // Use the same navigation logic as handleNext when status is settlement
          const nextPage = welcomeSettings?.startWithPayment
            ? applyAfterCapture
              ? 'frame'
              : 'filter'
            : mode === 'capture'
              ? 'capture'
              : 'record'
          
          navigate(`/${projectId}/${nextPage}`)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return (): void => {
      clearInterval(interval)
    }
  }, [isProcessingVoucher, navigate, projectId, welcomeSettings, mode, getPageSettings])

  const handlePromoEnter = async (code: string): Promise<void> => {
    setPromoError('')
    setIsPromoLoading(true)
    try {
      // Check subscription type
      // if (
      //   subscriptionType === 'free' ||
      //   subscriptionType === 'starter' ||
      //   subscriptionType === null
      // ) {
      //   const activityLog: ActivityLog = {
      //     page: 'checkout',
      //     type: 'interaction',
      //     action: 'failure',
      //     element: 'promo-code',
      //     details: {
      //       code,
      //       message: "Promo isn't available for this device"
      //     },
      //     timestamp
      //   }

      //   setSessionState({
      //     timeline: {
      //       activityLogs: [activityLog]
      //     }
      //   })

      //   cogoToast.error("Promo isn't available for this device")
      //   return
      // }

      const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code,
          projectId,
          amount: calculateSubtotal()
        })
      })

      const data = await response.json().catch(() => ({}))
      const isValid = response.ok && data.isValid
      const details = {
        code,
        message: data.message,
        isCash: data.isCash
      }
      logActivity(
        isValid
          ? `Promo code "${code}" applied successfully.`
          : `Failed to apply promo code "${code}".`,
        details
      )

      // Calculate final amount after discount (separate cash vs discount paths)
      const subtotal = calculateSubtotal()
      const apiDiscount = roundMoney(data.discount || 0, projectCurrency)
      const isCash = Boolean(isValid && data.isCash)
      const finalAmount = isValid
        ? isCash
          ? subtotal // Cash: revenue = full session price
          : roundMoney(Math.max(0, subtotal - apiDiscount), projectCurrency)
        : calculateTotal()
      const calculatedDiscount = isValid
        ? isCash
          ? 0 // Cash voucher: no discount; amount = subtotal (revenue)
          : finalAmount === 0
            ? subtotal // 100% discount
            : apiDiscount > 0
              ? apiDiscount
              : 0
        : 0

      // If final amount is 0 (or cash voucher), build packages array and prepare for auto-navigation
      const packagesInCalculation: Package[] = []
      if (isValid && (finalAmount === 0 || isCash)) {
        // Build selectedPackages array to match the actual calculation (same as handleNext)
        if (mode === 'capture') {
          if (printPackages.length > 0) {
            const selectedPrintPackage = getSelectedPrintPackage()
            if (selectedPrintPackage) packagesInCalculation.push(selectedPrintPackage)
          }
          if (gifPackage && userPreferences.useGIF && gifPackage) {
            packagesInCalculation.push(gifPackage)
          }
          if (recordingPackage && userPreferences.useRecording && recordingPackage) {
            packagesInCalculation.push(recordingPackage)
          }
          if (digitalPackage && userPreferences.useDigital && digitalPackage) {
            packagesInCalculation.push(digitalPackage)
          }
          if (videoPackage && userPreferences.useVideo) {
            packagesInCalculation.push(videoPackage)
          }
          if (paxPackages.length > 0 && userPreferences.usePax) {
            const selectedPaxPackage = getSelectedPaxPackage()
            if (selectedPaxPackage) packagesInCalculation.push(selectedPaxPackage)
          }
        } else if (mode === 'record' && videoPackage && userPreferences.useVideo && videoPackage) {
          packagesInCalculation.push(videoPackage)
        }
      }

      const updates: DeepPartial<SessionStateData> = {
        transaction: {
          amount: isValid ? finalAmount : undefined,
          discount: isValid ? (calculatedDiscount > 0 ? calculatedDiscount : 0) : undefined,
          voucherCode: isValid ? code : undefined,
          voucherId: isValid && data.promoId ? data.promoId : undefined,
          voucherType:
            isValid && data.isCash ? 'cash' : isValid && data.type ? data.type : undefined,
          voucherValue: isValid && data.value != null ? data.value : undefined,
          // Cash voucher or 100% discount: already paid (settlement)
          status: isValid && (data.isCash || finalAmount === 0) ? 'settlement' : undefined,
          ...(isValid && (finalAmount === 0 || isCash)
            ? { selectedPackages: packagesInCalculation }
            : {})
        },
        // Update user preferences if final amount is 0 or cash (settlement)
        ...(isValid && (finalAmount === 0 || isCash)
          ? {
              userPreferences: {
                copies: selectedPrintQuantity,
                usePrint: selectedPrintQuantity > 0
              }
            }
          : {})
      }

      setSessionState(updates)

      if (isValid) {
        setPromoError('')
        cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
        setShowPromoInput(false)
        setPromoCode(code) // Update local state to display in UI
        
        // If final amount is 0, show processing indicator and auto-navigate after 3 seconds
        if (finalAmount === 0) {
          // Wait for state update to propagate
          await Promise.resolve()

          // Log activity
          const details = {
            copies: selectedPrintQuantity,
            amount: finalAmount,
            package: packagesInCalculation.map((pkg) => pkg.type).join(', '),
            packageDescription: packagesInCalculation.map((pkg) => pkg.description).join(', '),
            promoApplied: true,
            promoCode: code
          }
          logActivity('Checkout complete with free promo code.', details)

          // Delay session upload to allow more activity logs to be collected
          setTimeout(() => {
            stateUploader.uploadSessionState(id)
          }, 5000)

          // Show processing indicator and start countdown
          setIsProcessingVoucher(true)
          setVoucherProcessingCountdown(3)
        }
      } else {
        const errorMessage =
          data.message || data.error || t('checkout.invalidPromo', 'Invalid promo code')
        setPromoError(errorMessage)
        cogoToast.error(errorMessage)
      }
    } catch (error) {
      console.error('Error validating promo code:', error)
      const errorMessage = t(
        'checkout.promoValidationFailed',
        'Failed to validate promo code. Please try again.'
      )
      setPromoError(errorMessage)
      cogoToast.error(errorMessage)
    } finally {
      setIsPromoLoading(false)
    }
  }

  // Once a voucher/promo has fully settled the transaction (cash voucher or
  // 100% discount), the paid amount and package selection must stay frozen.
  // Otherwise the customer can add/increase items (e.g. print copies) for
  // free after the session is already marked as paid, since there is no
  // further payment step to collect the difference. Production evidence:
  // sessions ub2y47 (amount inflated past the voucher cap) and f27g86
  // (copies incremented past what the voucher covered).
  const isOrderLockedByVoucher = (): boolean => {
    if (transaction.status === 'settlement') {
      cogoToast.warn(
        t(
          'checkout.orderLockedByVoucher',
          'Remove the applied voucher to change your order.'
        )
      )
      return true
    }
    return false
  }

  const handlePackageChange = (
    packageType: string,
    value: boolean | number,
    packageInfo: Package
  ): void => {
    if (isOrderLockedByVoucher()) return

    const details = {
      packageType,
      value,
      price: packageInfo.price
    }
    logActivity(`Package selection changed for ${packageInfo.description || packageType}.`, details)

    const updates: DeepPartial<SessionStateData> = {
      userPreferences: {
        [packageType === 'print'
          ? 'copies'
          : packageType === 'gif'
            ? 'useGIF'
            : packageType === 'digital'
              ? 'useDigital'
              : packageType === 'pax'
                ? 'usePax'
                : `use${packageType.charAt(0).toUpperCase() + packageType.slice(1)}`]: value,
        // If print quantity is 0, disable printing
        ...(packageType === 'print' && { usePrint: Number(value) > 0 }),
        // Ticking the recording is the moment it is bought, so the type the pipeline
        // gates on is set HERE, from the package actually ticked. Before, this set only
        // `useRecording`, and `recordingType` came from the init effect alone — which
        // skips once anything is selected and can run before `recordingPackage` has
        // resolved. A guest could then pay with `recordingType: null`: Capture built no
        // recorder, Select skipped the framed video, and nothing was logged.
        ...(packageType === 'recording' &&
          value &&
          isRecordingPackageType(packageInfo.type) && {
            recordingType: toRecordingType(packageInfo.type)
          })
      },
      transaction: {
        selectedPackages: value
          ? [...(transaction?.selectedPackages || []), packageInfo]
          : (transaction?.selectedPackages || []).filter((pkg) => pkg.type !== packageType),
        amount: calculateTotal(),
        // Stamp the project's currency at checkout-time so the Payment page,
        // session sync, and any voucher flow all use the same denomination.
        currency: projectCurrency
      }
    }

    setSessionState(updates)
  }

  const selectedPrintPackage = getSelectedPrintPackage()
  const selectedPaxPackage = getSelectedPaxPackage()

  const nextPage = paymentConfig?.settings?.enabled
    ? calculateTotal() === 0
      ? welcomeSettings?.startWithPayment
        ? 'filter'
        : mode === 'capture'
          ? 'capture'
          : 'record'
      : 'payment'
    : welcomeSettings?.startWithPayment
      ? 'filter'
      : mode === 'capture'
        ? 'capture'
        : 'record'

  return (
    <BackgroundContainer
      className={`flex h-full min-h-0 items-center justify-center ${
        checkoutNarrowLandscape
          ? 'overflow-hidden p-2'
          : useCompactChrome
            ? 'custom-scrollbar overflow-y-auto p-2'
            : 'overflow-hidden p-4'
      }`}
      data-page="checkout"
    >
      {/* Voucher Processing Overlay */}
      {isProcessingVoucher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div
            className={`mx-4 flex flex-col items-center rounded-2xl bg-white shadow-2xl ${
              checkoutNarrowLandscape
                ? 'max-w-[min(18rem,calc(100%-2rem))] gap-4 p-4'
                : useCompactChrome
                  ? 'max-w-md gap-6 p-6'
                  : 'max-w-md gap-6 p-12'
            }`}
          >
            <div className="relative">
              <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h2
                className={`mb-2 font-bold text-carbon ${useCompactChrome ? 'text-xl' : 'text-2xl'}`}
              >
                {t('checkout.voucherProcessing', 'Processing Voucher...')}
              </h2>
              <p className={`mb-4 text-gray-600 ${useCompactChrome ? 'text-base' : 'text-lg'}`}>
                {t('checkout.voucherProcessingMessage', 'Your order is free! Redirecting in')}
              </p>
              <div className="flex items-center justify-center gap-2">
                <span
                  className={`font-bold text-blue-500 ${useCompactChrome ? 'text-2xl' : 'text-4xl'}`}
                >
                  {voucherProcessingCountdown}
                </span>
                <span className={`text-gray-500 ${useCompactChrome ? 'text-lg' : 'text-xl'}`}>
                  {t('checkout.seconds', 'seconds')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      <GlassPanel
        className={
          checkoutNarrowLandscape
            ? 'mx-auto flex max-h-[min(92dvh,calc(100dvh-5rem))] w-full max-w-[min(20rem,100%)] flex-col items-stretch gap-1.5 overflow-hidden px-3 py-2'
            : `mx-auto flex w-full flex-col items-center ${useCompactChrome ? 'max-w-xl gap-2 p-3' : checkoutPortrait ? 'max-h-[min(92dvh,calc(100dvh-5rem))] max-w-3xl gap-8 overflow-hidden p-12' : 'max-h-[min(92dvh,calc(100dvh-5rem))] max-w-xl gap-5 overflow-hidden p-8'}`
        }
      >
        <h1
          className={`shrink-0 font-bold text-carbon ${
            checkoutNarrowLandscape
              ? 'mb-0.5 text-center text-lg'
              : useCompactChrome
                ? 'mb-2 text-2xl'
                : checkoutPortrait
                  ? 'mb-4 text-5xl'
                  : 'mb-2 text-3xl'
          }`}
        >
          {t('checkout.title', 'Review Your Order')}
        </h1>

        <div
          className={
            checkoutNarrowLandscape
              ? 'flex min-h-0 w-full flex-col gap-0'
              : useCompactChrome
                ? 'flex min-h-0 w-full flex-col gap-2'
                : 'flex min-h-0 w-full flex-col gap-0'
          }
        >
          <div className="custom-scrollbar flex min-h-0 w-full flex-col overflow-y-auto">
          <div className={`flex w-full flex-col items-stretch ${checkoutItemsListGap}`}>
          {/* Print Package Adjustment */}
          {mode === 'capture' && printPackages.length > 0 && (
            <div className={checkoutPkgRow}>
              <div className={checkoutPkgLead}>
                <div className={checkoutPkgIconClass}>
                  <IoPrint />
                </div>
                <div className={checkoutPkgTitleBlock}>
                  <h2 className={`${checkoutH2Class} truncate`}>
                    {t('checkout.print', 'Print')}
                  </h2>
                  <span className={`${checkoutSubClass} line-clamp-1`}>
                    {t('checkout.choosePrints', 'Choose number of prints')}
                  </span>
                </div>
              </div>

              <div className={checkoutPkgControl}>
                <div
                  className={`flex w-fit items-stretch rounded-md border ${checkoutRule}`}
                >
                  <button
                    type="button"
                    title={t('checkout.minus', 'Minus')}
                    className={checkoutStepperMinus}
                    onClick={() => {
                      if (isOrderLockedByVoucher()) return
                      const minQuantity =
                        printPackages.length > 0
                          ? Math.min(...printPackages.map((p) => p.quantity))
                          : 1
                      const newQuantity = Math.max(minQuantity, selectedPrintQuantity - 1)
                      const newPackage = printPackages.find((pkg) => pkg.quantity === newQuantity)
                      if (newPackage) {
                        handlePackageChange('print', newQuantity, newPackage)
                        setSelectedPrintQuantity(newQuantity)
                      }
                    }}
                  >
                    -
                  </button>
                  <span className={checkoutStepperMid}>{selectedPrintQuantity}</span>
                  <button
                    type="button"
                    title={t('checkout.plus', 'Plus')}
                    className={checkoutStepperPlus}
                    onClick={() => {
                      if (isOrderLockedByVoucher()) return
                      const maxQuantity = printPackages[printPackages.length - 1].quantity
                      const newQuantity = Math.min(maxQuantity, selectedPrintQuantity + 1)
                      const newPackage = printPackages.find((pkg) => pkg.quantity === newQuantity)
                      if (newPackage) {
                        handlePackageChange('print', newQuantity, newPackage)
                        setSelectedPrintQuantity(newQuantity)
                      }
                    }}
                  >
                    +
                  </button>
                </div>
                {selectedPrintPackage?.description && !useCompactChrome && (
                  <span className="mt-1 text-sm text-gray-500">
                    {selectedPrintPackage.description}
                  </span>
                )}
              </div>

              <span
                className={`${checkoutPriceClass} ${checkoutPkgPriceSpanAlign}`}
              >
                {selectedPrintPackage?.price && selectedPrintPackage.price > 0
                  ? formatPrice(selectedPrintPackage.price)
                  : t('checkout.free', 'Free')}
              </span>
            </div>
          )}

          {/* Digital Photos Package - show when digital exists and no print packages or user chose 0 prints */}
          {mode === 'capture' && digitalPackage && (
            <div className={checkoutPkgRow}>
              <div className={checkoutPkgLead}>
                <div className={checkoutPkgIconClass}>
                  <MdPhotoLibrary />
                </div>
                <div className={checkoutPkgTitleBlock}>
                  <h2 className={`${checkoutH2Class} truncate`}>
                    {t('checkout.digital', 'Digital Photos')}
                  </h2>
                  <span className={`${checkoutSubClass} line-clamp-1`}>
                    {t('checkout.getDigital', 'Get digital copies of your photos')}
                  </span>
                </div>
              </div>
              {digitalPackage && (
                <>
                  <div className={checkoutPkgToggleWrap}>
                    <input
                      title={t('checkout.digital', 'Digital Photos')}
                      type="checkbox"
                      className={`h-10 w-10 cursor-pointer accent-blue-600 ${
                        !digitalPackage.optional ? 'invisible' : ''
                      }`}
                      checked={userPreferences.useDigital}
                      onChange={() =>
                        digitalPackage &&
                        handlePackageChange('digital', !userPreferences.useDigital, digitalPackage)
                      }
                    />
                  </div>
                  <span
                    className={`${checkoutPriceClass} ${checkoutPkgPriceSpanAlign} ${
                      !userPreferences.useDigital && digitalPackage.optional ? 'opacity-50' : ''
                    }`}
                  >
                    {digitalPackage.price > 0
                      ? formatPrice(digitalPackage.price)
                      : t('checkout.free', 'Free')}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Pax Package Adjustment */}
          {mode === 'capture' && paxPackages.length > 0 && (
            <div className={checkoutPkgRow}>
              <div className={checkoutPkgLead}>
                <div className={checkoutPkgIconClass}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M12 4a4 4 0 0 1 4 4a4 4 0 0 1-4 4a4 4 0 0 1-4-4a4 4 0 0 1 4-4m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4"
                    />
                  </svg>
                </div>
                <div className={checkoutPkgTitleBlock}>
                  <h2 className={`${checkoutH2Class} truncate`}>
                    {t('checkout.pax', 'Number of People')}
                  </h2>
                  <span className={`${checkoutSubClass} line-clamp-1`}>
                    {t('checkout.choosePeople', 'Choose number of people')}
                  </span>
                </div>
              </div>

              <div className={checkoutPkgControl}>
                <div
                  className={`flex w-fit items-stretch rounded-md border ${checkoutRule}`}
                >
                  <button
                    type="button"
                    title={t('checkout.minus', 'Minus')}
                    className={checkoutStepperMinus}
                    onClick={() => {
                      const newQuantity = Math.max(1, selectedPaxQuantity - 1)
                      const newPackage =
                        paxPackages.find((pkg) => pkg.quantity === newQuantity) || paxPackages[0]
                      handlePackageChange('pax', newQuantity, newPackage)
                      setSelectedPaxQuantity(newQuantity)
                    }}
                  >
                    -
                  </button>
                  <span className={checkoutStepperMid}>{selectedPaxQuantity}</span>
                  <button
                    type="button"
                    title={t('checkout.plus', 'Plus')}
                    className={checkoutStepperPlus}
                    onClick={() => {
                      const maxQuantity = paxPackages[paxPackages.length - 1].quantity
                      const newQuantity = Math.min(maxQuantity, selectedPaxQuantity + 1)
                      const newPackage =
                        paxPackages.find((pkg) => pkg.quantity === newQuantity) || paxPackages[0]
                      handlePackageChange('pax', newQuantity, newPackage)
                      setSelectedPaxQuantity(newQuantity)
                    }}
                  >
                    +
                  </button>
                </div>
                {selectedPaxPackage?.description && !useCompactChrome && (
                  <span className="mt-1 text-sm text-gray-500">
                    {selectedPaxPackage.description}
                  </span>
                )}
              </div>

              <span
                className={`${checkoutPriceClass} ${checkoutPkgPriceSpanAlign}`}
              >
                {selectedPaxPackage?.price && selectedPaxPackage.price > 0
                  ? formatPrice(selectedPaxPackage.price)
                  : t('checkout.free', 'Free')}
              </span>
            </div>
          )}

          {/* GIF Package */}
          {mode === 'capture' && gifPackage && (
            <div className={checkoutPkgRow}>
              <div className={checkoutPkgLead}>
                <div className={checkoutPkgIconClass}>
                  <MdOutlineGifBox />
                </div>
                <div className={checkoutPkgTitleBlock}>
                  <h2 className={`${checkoutH2Class} truncate`}>{t('checkout.gif', 'GIF')}</h2>
                  <span className={`${checkoutSubClass} line-clamp-1`}>
                    {t('checkout.getAnimated', 'Get animated image')}
                  </span>
                </div>
              </div>
              <div className={checkoutPkgToggleWrap}>
                <input
                  title={t('checkout.gif', 'GIF')}
                  type="checkbox"
                  className={`h-10 w-10 cursor-pointer accent-blue-600 ${!gifPackage.optional ? 'invisible' : ''}`}
                  checked={userPreferences.useGIF}
                  onChange={() =>
                    gifPackage && handlePackageChange('gif', !userPreferences.useGIF, gifPackage)
                  }
                />
              </div>
              <span
                className={`${checkoutPriceClass} ${checkoutPkgPriceSpanAlign} ${
                  !userPreferences.useGIF && gifPackage.optional ? 'opacity-50' : ''
                }`}
              >
                {gifPackage.price > 0
                  ? formatPrice(gifPackage.price)
                  : t('checkout.free', 'Free')}
              </span>
            </div>
          )}

          {/* Recording Package */}
          {mode === 'capture' && recordingPackage && (
            <div className={checkoutPkgRow}>
              <div className={checkoutPkgLead}>
                <div className={checkoutPkgIconClass}>
                  <FaVideo />
                </div>
                <div className={checkoutPkgTitleBlock}>
                  <h2 className={`${checkoutH2Class} truncate`}>
                    {t('recording.title')} -{' '}
                    {t(
                      `recording.modes.${
                        recordingType === 'strip_live'
                          ? 'live'
                          : recordingType === 'strip_story'
                            ? 'story'
                            : recordingType === 'session_recording'
                              ? 'session'
                              : 'live'
                      }`
                    )}
                  </h2>
                  <span className={`${checkoutSubClass} line-clamp-1`}>
                    {getRecordingDescription(recordingType || 'strip_live')}
                  </span>
                </div>
              </div>
              <div className={checkoutPkgToggleWrap}>
                <input
                  title={t('recording.title')}
                  type="checkbox"
                  className={`h-10 w-10 cursor-pointer accent-blue-600 ${!recordingPackage.optional ? 'invisible' : ''}`}
                  checked={userPreferences.useRecording}
                  onChange={() =>
                    recordingPackage &&
                    handlePackageChange(
                      'recording',
                      !userPreferences.useRecording,
                      recordingPackage
                    )
                  }
                />
              </div>
              <span
                className={`${checkoutPriceClass} ${checkoutPkgPriceSpanAlign} ${
                  !userPreferences.useRecording && recordingPackage.optional ? 'opacity-50' : ''
                }`}
              >
                {recordingPackage.price > 0
                  ? formatPrice(recordingPackage.price)
                  : t('checkout.free')}
              </span>
            </div>
          )}

          {/* Video Package */}
          {videoPackage && (
            <div className={checkoutPkgRow}>
              <div className={checkoutPkgLead}>
                <div className={checkoutPkgIconClass}>
                  <MdOutlineVideoLibrary />
                </div>
                <div className={checkoutPkgTitleBlock}>
                  <h2 className={`${checkoutH2Class} truncate`}>{t('checkout.video', 'Video')}</h2>
                  <span className={`${checkoutSubClass} line-clamp-1`}>
                    {t('checkout.recordVideo', 'Record a video')}
                  </span>
                </div>
              </div>
              <div className={checkoutPkgToggleWrap}>
                {videoPackage.optional && (
                  <input
                    type="checkbox"
                    title={t('checkout.video', 'Video')}
                    className="h-10 w-10 cursor-pointer accent-blue-600"
                    checked={userPreferences.useVideo}
                    onChange={() =>
                      videoPackage &&
                      handlePackageChange('video', !userPreferences.useVideo, videoPackage)
                    }
                  />
                )}
              </div>
              <span
                className={`${checkoutPriceClass} ${checkoutPkgPriceSpanAlign} ${
                  !userPreferences.useVideo && videoPackage.optional ? 'opacity-50' : ''
                }`}
              >
                {videoPackage.price > 0
                  ? formatPrice(videoPackage.price)
                  : t('checkout.free', 'Free')}
              </span>
            </div>
          )}
          </div>
          </div>

          {/* Pricing Summary */}
          <div
            className={`shrink-0 border-t border-gray-400 ${checkoutNarrowLandscape ? 'mt-0 pt-2' : useCompactChrome ? 'mt-2 pt-3' : checkoutPortrait ? 'mt-4 pt-6' : 'mt-3 pt-5'}`}
          >
            {/* Applied Promo Code/Voucher Badge */}
            {((transaction.voucherCode ?? transaction.promoCode) || transaction.voucherType) && (
              <div
                className={`bg-green-50 border border-green-200 rounded-lg ${checkoutNarrowLandscape ? 'mb-2 p-2' : useCompactChrome ? 'mb-4 p-3' : 'mb-3 p-2.5'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg
                      className={`text-green-600 ${checkoutNarrowLandscape || useCompactChrome ? 'h-5 w-5' : 'h-4 w-4'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span
                      className={`font-medium text-green-800 ${checkoutNarrowLandscape || useCompactChrome ? 'text-base' : 'text-sm'}`}
                    >
                      {transaction.voucherType === 'cash'
                        ? t('checkout.cashVoucherApplied', 'Cash Voucher Applied')
                        : (transaction.voucherCode ?? transaction.promoCode)
                          ? `${t('checkout.promoCodeApplied', 'Promo Code')}: ${(transaction.voucherCode ?? transaction.promoCode)!.toUpperCase()}`
                          : t('checkout.voucherApplied', 'Voucher Applied')}
                    </span>
                  </div>
                  {(transaction.voucherCode ?? transaction.promoCode) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSessionState({
                          transaction: {
                            discount: undefined,
                            voucherCode: undefined,
                            voucherId: undefined,
                            voucherType: undefined,
                            voucherValue: undefined,
                            status: undefined
                          }
                        })
                        setPromoCode('')
                        cogoToast.info(t('checkout.promoRemoved', 'Promo code removed'))
                      }}
                      className="text-sm text-green-600 hover:text-green-800 underline"
                      title={t('checkout.removePromo', 'Remove promo code')}
                    >
                      {t('checkout.remove', 'Remove')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Frame add-on (editor-driven per-frame surcharge, once per order) */}
            {mode === 'capture' && frameAddon > 0 && selectedPrintQuantity > 0 && (
              <div className="flex justify-between w-full mb-2">
                <span className={checkoutSummaryLabel}>
                  {t('frameSurcharge.lineItem', 'Frame add-on')}
                </span>
                <span className={checkoutPriceClass}>
                  {t('frameSurcharge.badge', '+{{price}}', { price: formatPrice(frameAddon) })}
                </span>
              </div>
            )}

            {/* Frame category base price (editor-driven per-category, once per order) */}
            {mode === 'capture' && frameCategoryBase > 0 && selectedPrintQuantity > 0 && (
              <div className="flex justify-between w-full mb-2">
                <span className={checkoutSummaryLabel}>
                  {userPreferences.frameCategoryNameForBase ||
                    t('frameCategoryBasePrice.lineItem', 'Base price')}
                </span>
                <span className={checkoutPriceClass}>
                  {t('frameCategoryBasePrice.badge', '+{{price}}', {
                    price: formatPrice(frameCategoryBase)
                  })}
                </span>
              </div>
            )}

            {/* Subtotal */}
            <div className="flex justify-between w-full mb-2">
              <span className={checkoutSummaryLabel}>{t('checkout.subtotal', 'Subtotal:')}</span>
              <span
                className={
                  transaction.discount && transaction.discount > 0
                    ? checkoutSummaryAmountStruck
                    : checkoutPriceClass
                }
              >
                {formatPrice(calculateSubtotal())}
              </span>
            </div>

            {/* Discount (if available) */}
            {transaction.discount !== undefined &&
            transaction.discount !== null &&
            transaction.discount > 0 ? (
              <div className="flex justify-between w-full mb-2">
                <div className="flex items-center gap-2">
                  <span className={checkoutDiscountLabel}>
                    {t('checkout.discount', 'Discount:')}
                  </span>
                  {transaction.voucherType === 'cash' && (
                    <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">
                      {t('checkout.cashVoucher', 'Cash Voucher')}
                    </span>
                  )}
                </div>
                <span className={checkoutDiscountAmt}>
                  -{formatPrice(transaction.discount)}
                </span>
              </div>
            ) : null}

            {/* Total Price */}
            <div
              className={`flex justify-between w-full border-t border-gray-400 py-2 ${
                checkoutNarrowLandscape ? 'mt-1 pt-2' : useCompactChrome ? 'mt-3 pt-3' : checkoutPortrait ? 'mt-6 pt-6' : 'mt-5 pt-5'
              }`}
            >
              <span className={checkoutTotalLabel}>{t('checkout.total', 'Total:')}</span>
              <span
                className={`${checkoutTotalAmount} ${
                  calculateTotal() === 0 ? '!text-green-600' : ''
                }`}
              >
                {calculateTotal() === 0
                  ? t('checkout.free', 'FREE')
                  : formatPrice(calculateTotal())}
              </span>
            </div>

            {checkoutTimeout > 0 && (
              <div
                className={`${checkoutTimeFooter} ${
                  isCountdownUrgent(remainingTime) ? '!font-bold !text-red-600' : ''
                }`}
              >
                {remainingTime}
              </div>
            )}
          </div>
        </div>

          <div
            className={`flex w-full flex-col shrink-0 ${checkoutNarrowLandscape ? 'items-stretch mt-1.5 gap-1.5' : useCompactChrome ? 'items-stretch mt-2 gap-2' : checkoutPortrait ? 'items-center mt-6 gap-4' : 'items-center mt-4 gap-3'}`}
          >
            {/*
              In a voucher-only region the primary CTA already becomes
              "Enter Voucher Code", so we hide the secondary
              "Have a voucher code?" affordance to avoid a duplicate.
              No banner here either — that messaging is for the photobooth
              owner during dashboard setup, not customers at the kiosk.
              Operator bypass lives in the top-right Skip Payment button.
            */}
            {config?.settings.promoEnabled && !showPromoInput && !isVoucherOnlyRegion && (
              <Button
                text={t('checkout.havePromo', 'Have a voucher code?')}
                variant="secondary"
                onClick={() => {
                  setPromoError('')
                  setShowPromoInput(true)
                }}
                className={checkoutSecondaryBtn}
              />
            )}

            <Button
              text={
                isVoucherOnlyRegion && transaction.status !== 'settlement'
                  ? t('checkout.enterVoucher', 'Enter Voucher Code')
                  : paymentConfig?.settings?.enabled
                    ? t('checkout.proceedToPayment', 'Proceed to Payment')
                    : t('common.continue', 'Continue')
              }
              onClick={
                isVoucherOnlyRegion && transaction.status !== 'settlement'
                  ? (): void => {
                      setPromoError('')
                      setShowPromoInput(true)
                    }
                  : handleNext
              }
              data-nav-to={nextPage}
              className={checkoutMainBtn}
            />
          </div>
      </GlassPanel>

      {showPromoInput && (config?.settings.voucherEnabled ?? config?.settings.promoEnabled) && (
        <OnScreenKeyboard
          onEnter={handlePromoEnter}
          placeholder={t('checkout.voucherCodePlaceholder', 'Type or paste your code')}
          error={promoError}
          isLoading={isPromoLoading}
          onInputChange={() => setPromoError('')}
          onClose={() => {
            setShowPromoInput(false)
            setPromoCode('')
            setPromoError('')
          }}
        />
      )}
    </BackgroundContainer>
  )
}

export default CheckoutPage
